import { useState } from 'react';

import { notify } from '../../app';
import { getStepDetails } from '../../api';
import { useLogo, useFetch, useRequest } from '../../hooks';
import { startQuiz, completeQuiz } from '../../api';
import { getCourse, getEnrollment } from '../../api';
import type { CompleteQuizResult } from '../../api';

export type { CourseType, LessonType } from '../../api';

export type AnswersContainerType = { [x in string]: string };

export const useQuiz = (enrollmentId: string, stepId: string) => {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswersContainerType>({});
  const [quizResult, setQuizResult] = useState<CompleteQuizResult>();

  const { data: enrollment, intializing: loadingEnrollment } = useFetch(
    () => getEnrollment(enrollmentId),
    [enrollmentId]
  );

  const courseId = enrollment?.courseId;
  const { intializing: loadingCourse, data: course } = useFetch(
    () => (courseId ? getCourse(courseId) : null),
    [courseId],
    { skip: !enrollment?.id }
  );
  const { intializing: loadingCourseDetails, data: courseStep } = useFetch(
    () => (courseId && stepId ? getStepDetails(courseId, stepId) : null),
    [courseId, stepId],
    { skip: !courseId || !stepId }
  );
  const { intializing: loadingQuiz, data: quiz } = useFetch(
    () => {
      if (!enrollmentId || !stepId) return null;

      return startQuiz(enrollmentId, stepId);
    },
    [enrollmentId, stepId],
    {
      onSuccess: () => {
        setQuestionIndex(0);
      },
    }
  );

  const questionId = quiz?.questions?.[questionIndex]?.questionId;
  const canGoLeft = questionIndex > 0;
  const canGoRight = questionIndex < (quiz?.questions?.length || 0) - 1;

  const handleAnswerQuestion = (questionId: string, answer: string) => {
    return setAnswers(old => ({ ...old, [questionId]: answer }));
  };
  const handleGoLeft = () => {
    if (!canGoLeft) return;
    setQuestionIndex(old => old - 1);
  };
  const handleGoRight = () => {
    if (!canGoRight) return;
    setQuestionIndex(old => old + 1);
  };

  const [handleFinishQuiz, finishing] = useRequest(
    () => {
      return completeQuiz(enrollmentId, stepId, {
        attemptId: quiz?.id!,
        completedAnswers: Object.entries(answers).map(
          ([questionId, answerText]) => ({
            questionId: questionId,
            answerText: answerText,
            optionNumber:
              quiz?.questions
                ?.find(q => q.questionId === questionId)
                ?.options?.findIndex(o => o === answerText) || 0,
          })
        ),
      });
    },
    {
      deps: [answers, quiz, enrollmentId, stepId],
      onSuccess: result => {
        result.passed
          ? notify.success(`Quiz passed`)
          : notify.error('Quiz failed');

        setQuizResult(result);
      },
      onError: (result: any) => {
        const reason = result?.body?.detail || 'Unknown reason';
        notify.error(`Failed to finish quiz: ${reason}`);
      },
    }
  );

  useLogo(course);

  return {
    quiz,
    course,
    enrollment,

    courseStep,

    answers,
    handleGoLeft,
    handleGoRight,
    handleAnswerQuestion,
    handleFinishQuiz,

    canGoLeft,
    canGoRight,
    questionId,
    questionIndex,

    quizResult,

    finishing,
    loading:
      loadingCourse || loadingQuiz || loadingEnrollment || loadingCourseDetails,
  };
};
